import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { openConsultationModal } from "../components/ConsultationModal";
import { GuardianHeroAnimation } from "../components/GuardianHeroAnimation";
import { FourEnginesOrbit } from "../components/FourEnginesOrbit";
import { GuardianRealtimeSlider } from "../components/GuardianRealtimeSlider";
import {
  ShieldCheck, Terminal, Cpu, Zap, Lock, Server, Activity,
  Database, AlertTriangle, CheckCircle2, ArrowRight, ArrowLeft, ExternalLink,
  Layers, Radio, FileText, Check, RefreshCw, Globe,
  Bell, Code2, FolderLock, Clock, HardDrive, Key,
  Network, Eye, ShieldAlert, Bug, ChevronRight, ChevronDown, ChevronUp, CheckCircle, Flame,
  Workflow, ArrowDown, Play, BarChart3, Search, Sparkles, User, Wrench, Shield, RotateCw
} from "lucide-react";

/* 01: Small Trust Points */
const TRUST_POINTS = [
  "Autonomous Threat Response",
  "Real-Time Infrastructure Monitoring",
  "In-House Data & Telemetry",
];

/* ── 02 — 5-Stage Autonomous Loop (Matching Exact Reference Timeline) ─────── */
const AUTONOMOUS_LOOP = [
  {
    step: "01",
    name: "Detect",
    desc: "Real-time anomaly & threat discovery across running hosts and applications.",
    icon: Search,
    highlight: false,
  },
  {
    step: "02",
    name: "Understand",
    desc: "Automated analysis of process behavior and resource patterns.",
    icon: BarChart3,
    highlight: false,
  },
  {
    step: "03",
    name: "Contain",
    desc: "Instant workload isolation preventing lateral escalation.",
    icon: ShieldCheck,
    highlight: true, // Glowing orange node as in reference!
  },
  {
    step: "04",
    name: "Remediate",
    desc: "Autonomous execution of verified self-healing runbooks.",
    icon: Cpu,
    highlight: false,
  },
  {
    step: "05",
    name: "Recover",
    desc: "Zero-downtime restoration to a verified operational baseline.",
    icon: RefreshCw,
    highlight: false,
  },
];

/* Enterprise FAQs */
const GUARDIAN_FAQS = [
  {
    q: "How does Guardian differ from traditional monitoring tools like Datadog or Prometheus?",
    a: "Traditional monitoring tools are passive observers: they alert your on-call engineers via Slack or PagerDuty when CPU spikes or a breach occurs, requiring 3:00 AM human triage. Guardian is an active autonomous defense layer. It not only detects anomalies, but immediately isolates offending workloads, terminates rogue processes, applies sovereign containment rules, and triggers self-healing runbooks in under 2 seconds."
  },
  {
    q: "Can Guardian protect workloads on our existing VPS or cloud accounts (AWS, Hetzner, DigitalOcean)?",
    a: "Yes. Guardian supports 'Bring Your Own VPS' (BYO-VPS) as well as fully managed NeuraVolt Bare Metal. You simply connect your Linux host (Ubuntu, Debian, AlmaLinux, Rocky) via a lightweight, secure agent, and Guardian takes over active telemetry and defense without migrating your data."
  },
  {
    q: "Does Guardian introduce significant CPU or RAM overhead to our production servers?",
    a: "No. Guardian operates with sub-0.5% CPU overhead and less than 35MB resident memory footprint. It utilizes kernel-level telemetry and event-driven watchers rather than brute-force polling, ensuring zero impact on your customer-facing response times."
  },
  {
    q: "What development stacks and runtimes are natively supported?",
    a: "Guardian provides out-of-the-box runtime health checks, memory leak watchdogs, and threat shields for Node.js, Next.js, Python (FastAPI/Django), Go, Rust, Kubernetes clusters, Docker containers, and high-performance web servers (Nginx/Caddy)."
  },
  {
    q: "How does governed remediation prevent accidental service downtime?",
    a: "Guardian categorizes operational actions into safe non-destructive healing (e.g., service restart, cache flushing, disk cleanup) which run autonomously, and high-impact interventions (e.g., port blocking, workload isolation, rollbacks) which can enforce cryptographic approval controls or pre-configured policy rules."
  }
];

/* ── 03 — Key Capabilities (6 Layers of Protection) ──────────────────────── */
const KEY_CAPABILITIES = [
  {
    icon: ShieldCheck,
    title: "Active Threat Defense",
    desc: "Detect and isolate suspicious activity before it becomes a larger production incident.",
    accent: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    icon: Activity,
    title: "Autonomous SRE",
    desc: "Identify infrastructure problems such as resource exhaustion, service failures and abnormal system behaviour and trigger guided remediation.",
    accent: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    icon: Lock,
    title: "Governed Remediation",
    desc: "Critical infrastructure actions can require elevated authorization before execution, keeping powerful operations under controlled access.",
    accent: "text-purple-600",
    bg: "bg-purple-50",
  },
  {
    icon: Database,
    title: "Recovery & Resilience",
    desc: "Protect critical workloads with recovery workflows and resilient backup strategies designed to reduce downtime and data-loss risk.",
    accent: "text-[#FF5A1F]",
    bg: "bg-orange-50",
  },
  {
    icon: Code2,
    title: "Cloud-Native & Container Defense",
    desc: "Engineered for cloud-native architectures including Kubernetes, Docker, Node.js, Python, Go, and distributed microservices.",
    accent: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  {
    icon: Server,
    title: "Fleet Operations",
    desc: "Manage infrastructure across managed environments and customer-owned VPS deployments from a unified operational layer.",
    accent: "text-cyan-600",
    bg: "bg-cyan-50",
  },
];

/* ── 05 — Four Defense Engines ───────────────────────────────────────────── */
const FOUR_ENGINES = [
  {
    num: "01",
    shortName: "Threat Containment",
    title: "Active Threat Containment",
    sub: "Detect and contain threats automatically.",
    desc: "Guardian continuously watches for suspicious activity and can isolate affected workloads while preserving the information needed for investigation and recovery.",
    tags: ["Threat Detection", "Automated Containment", "Forensics Preservation", "Micro-Quarantine"],
    dotColor: "bg-[#FF5A1F]",
    accentColor: "text-[#FF5A1F]",
    borderColor: "border-[#FF5A1F]",
    badge: "Active Shield",
    icon: ShieldCheck,
    metrics: [
      { label: "Response Latency", value: "< 1.4s" },
      { label: "Isolation Protocol", value: "eBPF Kernel Lock" },
      { label: "Workload Status", value: "Quarantined" }
    ],
    terminalStatus: "THREAT MITIGATED",
    terminalStatusColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    logs: [
      { time: "14:22:01.120", level: "DETECT", msg: "Unrecognized child process spawned on pod/auth-service" },
      { time: "14:22:01.350", level: "ACTION", msg: "eBPF rule triggered: lateral egress blocked on interface eth0" },
      { time: "14:22:01.780", level: "VAULT", msg: "Process memory core dumped to sovereign forensics store" },
      { time: "14:22:02.100", level: "RESTORE", msg: "Healthy clone spun up; ingress re-routed with 0 dropped requests" },
    ],
    graphPoints: [20, 25, 22, 95, 45, 18, 12, 10, 11, 10]
  },
  {
    num: "02",
    shortName: "Autonomous SRE",
    title: "Autonomous SRE & Self-Healing",
    sub: "Keep production infrastructure healthy without 3:00 AM alerts.",
    desc: "Monitor system resources, processes and service behaviour to identify failures and abnormal conditions before they become prolonged outages, automatically executing verified runbooks.",
    tags: ["Resource Saturation Watchdog", "Memory Leak Recycling", "OOM Killer Intercept", "Self-Healing Playbooks"],
    dotColor: "bg-emerald-500",
    accentColor: "text-emerald-500",
    borderColor: "border-emerald-500",
    badge: "SRE Health",
    icon: Activity,
    metrics: [
      { label: "Auto-Remediation", value: "99.4%" },
      { label: "Active Playbooks", value: "42+ Runbooks" },
      { label: "Resolution Time", value: "< 2.0s" }
    ],
    terminalStatus: "RUNTIME OPTIMIZED",
    terminalStatusColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    logs: [
      { time: "08:14:12.190", level: "ANOMALY", msg: "Node.js worker memory climbing at +45MB/min (threshold: 85%)" },
      { time: "08:14:12.410", level: "RUNBOOK", msg: "Executing Playbook #SRE-09: Rolling graceful worker recycle" },
      { time: "08:14:13.200", level: "HEALTHZ", msg: "Readiness probe 200 OK across all active worker threads" },
      { time: "08:14:13.550", level: "RESOLVED", msg: "Memory baseline restored to 28% without connection termination" },
    ],
    graphPoints: [30, 45, 65, 88, 92, 40, 28, 25, 26, 27]
  },
  {
    num: "03",
    shortName: "Data Resilience",
    title: "Resilient Data Protection",
    sub: "Prepare for the worst-case scenario with immutable recovery.",
    desc: "Build isolated recovery mechanisms that help protect critical data from accidental deletion, infrastructure failure and ransomware scenarios with verified cryptographic baselines.",
    tags: ["Air-Gapped Vaults", "Point-in-Time Rollback", "Ransomware Invariant", "Automated Restore Verification"],
    dotColor: "bg-blue-500",
    accentColor: "text-blue-500",
    borderColor: "border-blue-500",
    badge: "Data Resilience",
    icon: Database,
    metrics: [
      { label: "RPO Target", value: "< 15 Mins" },
      { label: "Integrity Check", value: "SHA-256 Verified" },
      { label: "Vault State", value: "Write-Once Air-Gapped" }
    ],
    terminalStatus: "VAULT SECURED",
    terminalStatusColor: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    logs: [
      { time: "02:00:00.012", level: "BACKUP", msg: "Encrypted volume block delta snapshot initialized" },
      { time: "02:00:14.300", level: "VERIFY", msg: "Checksum hash sha256:7f3a9e verified across 1.4TB cluster" },
      { time: "02:00:15.110", level: "AIR-GAP", msg: "Replication confirmed on secondary isolated sovereign storage" },
      { time: "02:00:15.890", level: "LOCKED", msg: "Cryptographic snapshot immutability lock engaged (7-year policy)" },
    ],
    graphPoints: [10, 15, 12, 14, 80, 85, 30, 12, 10, 10]
  },
  {
    num: "04",
    shortName: "Governed Fleet",
    title: "Governed Fleet Operations",
    sub: "Powerful infrastructure actions. Controlled by design.",
    desc: "Guardian provides centralized infrastructure operations with role-based authorization, multi-party approval quorums, and immutable audit trails for sensitive remediation actions.",
    tags: ["Role-Based Quorums", "Cryptographic Audit Log", "Multi-VPS Control Plane", "Policy Enforced Gates"],
    dotColor: "bg-amber-500",
    accentColor: "text-amber-500",
    borderColor: "border-amber-500",
    badge: "Access Governance",
    icon: Lock,
    metrics: [
      { label: "Fleet Coverage", value: "100% Unified" },
      { label: "Audit Integrity", value: "Cryptographic Tamper-Proof" },
      { label: "Approval Quorum", value: "Multi-Party Required" }
    ],
    terminalStatus: "FLEET SYNCHRONIZED",
    terminalStatusColor: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    logs: [
      { time: "19:30:45.020", level: "REQUEST", msg: "Engineer requested global firewall policy mutation" },
      { time: "19:30:45.310", level: "QUORUM", msg: "Governance rule triggered: Requires 2-of-3 SecOps approvals" },
      { time: "19:30:47.880", level: "AUTH", msg: "Cryptographic hardware token confirmed by SecOps Lead" },
      { time: "19:30:48.600", level: "DEPLOY", msg: "Policy broadcasted to 64 customer VPS nodes in parallel (0 errors)" },
    ],
    graphPoints: [15, 20, 18, 22, 25, 75, 50, 22, 20, 19]
  },
];

/* ── 07 — Deployment Options ─────────────────────────────────────────────── */
const DEPLOYMENT_MODES = [
  {
    num: "01",
    title: "NeuraVolt Managed Infrastructure",
    desc: "Managed infrastructure with Guardian integrated into the operational environment.",
    bestFor: "Agencies, SaaS teams and businesses that want managed infrastructure.",
    highlight: "Turnkey Performance",
  },
  {
    num: "02",
    title: "Bring Your Own VPS",
    desc: "Connect your existing infrastructure and manage it through Guardian.",
    bestFor: "Teams already running workloads on their preferred cloud or VPS provider.",
    highlight: "Most Popular",
    isPrimary: true,
  },
  {
    num: "03",
    title: "Sovereign / On-Premises",
    desc: "Deploy within your own controlled infrastructure for environments requiring greater data and network isolation.",
    bestFor: "Regulated, enterprise and sensitive workloads.",
    highlight: "Maximum Isolation",
  },
];

/* ── 09 — Who is Guardian for? ───────────────────────────────────────────── */
const TARGET_AUDIENCE = [
  {
    title: "Agencies & Dev Shops",
    desc: "Manage multiple client environments without manually checking every server.",
    icon: Globe,
  },
  {
    title: "SaaS & E-Commerce",
    desc: "Reduce the operational impact of infrastructure failures and security incidents.",
    icon: Zap,
  },
  {
    title: "DevOps Teams",
    desc: "Automate repetitive infrastructure monitoring and remediation workflows.",
    icon: Workflow,
  },
  {
    title: "Technology Leaders",
    desc: "Bring monitoring, security and SRE operations into one controlled platform.",
    icon: ShieldCheck,
  },
];

export default function Guardian() {
  const [activeEngine, setActiveEngine] = useState(0);
  const [activeConsoleTab, setActiveConsoleTab] = useState("overview");
  const [openFaq, setOpenFaq] = useState(null);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "NeuraVolt Guardian",
    "alternateName": "Guardian Autonomous SRE & Active Defense",
    "headline": "Not Just Monitoring. Autonomous Infrastructure Defense.",
    "description": "NeuraVolt Guardian continuously monitors your servers, detects threats, responds to infrastructure failures, and helps restore production without waiting for a 3:00 AM manual intervention.",
    "applicationCategory": "SecurityApplication, InfrastructureApplication",
    "operatingSystem": "Linux, Cloud, Bare Metal, Docker",
    "url": "https://neuravolt.in/services/cybersecurity",
    "provider": {
      "@type": "Organization",
      "name": "Neuravolt Technology",
      "legalName": "VOLTNOVATE SOLUTIONS LLP",
      "url": "https://neuravolt.in"
    }
  };

  const location = useLocation();
  const isCybersecurityService = location.pathname === "/services/cybersecurity";
  const seoTitle = isCybersecurityService
    ? "Cybersecurity Consulting & Server Security Services | NeuraVolt Guardian"
    : "NeuraVolt Guardian: Server Security Monitoring Software & Autonomous SRE | NeuraVolt";
  const seoDesc = isCybersecurityService
    ? "Zero-trust network defense, proactive vulnerability mitigation, and autonomous self-healing SRE infrastructure services engineered by NeuraVolt."
    : "NeuraVolt Guardian continuously monitors server fleets, contains zero-day vulnerabilities, auto-heals memory leaks, and automates SRE infrastructure recovery.";
  const seoKeywords = isCybersecurityService
    ? "cybersecurity consulting services, cybersecurity company in Gwalior, server security monitoring, autonomous SRE, zero trust security, NeuraVolt"
    : "NeuraVolt Guardian, server security monitoring software, server fleet monitoring and security, autonomous server security, site reliability engineering tools, autonomous SRE, infrastructure defense";
  const canonicalPath = isCybersecurityService ? "/services/cybersecurity" : "/guardian";

  return (
    <Layout>
      <Seo
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        path={canonicalPath}
        jsonLd={jsonLd}
      />

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 01: HERO SECTION WITH GUARDIAN PRODUCT LOGO & 3D ANIMATION             */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="overview" className="scroll-mt-24 relative pt-32 pb-14 sm:pt-36 sm:pb-16 px-5 sm:px-8 bg-[#FAFAFA] border-b border-black/[0.06] overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

        {/* Ambient atmospheric orange glow behind 3D core */}
        <div className="absolute top-20 right-1/4 w-[500px] h-[350px] bg-gradient-to-bl from-orange-400/15 via-[#FF5A1F]/10 to-transparent blur-3xl pointer-events-none rounded-full" />
        <div className="absolute top-12 left-1/4 w-[400px] h-[250px] bg-gradient-to-tr from-blue-400/10 via-orange-300/10 to-transparent blur-3xl pointer-events-none rounded-full" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            
            {/* Left Column: Product Logo, Headline, Description & Dual CTAs */}
            <div className="lg:col-span-6 text-left">
              
              {/* Product Logo in Product Area */}
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="/brand/guardian-logo.png"
                  alt="NeuraVolt Guardian"
                  className="h-10 sm:h-12 w-auto object-contain drop-shadow-xs"
                />
              </div>

              {/* Eyebrow badge matching reference */}
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5A1F] shadow-sm shadow-[#FF5A1F]/60" />
                <span className="text-[11px] sm:text-xs font-mono font-bold tracking-[0.16em] text-neutral-800 uppercase">
                  THE OPERATIONAL PARADIGM
                </span>
              </div>

              {/* Main Headline matching reference */}
              <h1 className="text-4xl sm:text-5xl lg:text-[56px] xl:text-[62px] font-display font-extrabold text-[#0B0B0B] leading-[1.08] tracking-tight mb-6">
                Not Just Monitoring. <br />
                <span className="text-[#FF5A1F]">Autonomous <br className="hidden sm:inline" />Infrastructure Defense.</span>
              </h1>

              {/* Sub-headline description */}
              <p className="text-base sm:text-lg text-neutral-600 leading-relaxed mb-8 max-w-xl font-normal">
                Traditional monitoring tells your team something is wrong. Guardian is designed to go further. It combines infrastructure monitoring, active threat defense, automated remediation and recovery workflows into one unified operational layer.
              </p>

              {/* Dual CTAs matching reference: Solid orange pill & White pill with play button */}
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <button
                  onClick={() => openConsultationModal("Guardian Security Audit")}
                  className="px-7 py-3.5 sm:py-4 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white text-sm sm:text-base font-semibold transition-all shadow-lg shadow-[#FF5A1F]/25 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2.5 group"
                >
                  <span>Book a Security Audit</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <a
                  href="#how-it-works"
                  className="px-6 py-3.5 sm:py-4 rounded-full bg-white hover:bg-neutral-50 text-[#0B0B0B] text-sm sm:text-base font-semibold border border-neutral-200/90 shadow-xs flex items-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] group"
                >
                  <span>Explore Guardian</span>
                  <div className="w-6 h-6 rounded-full bg-neutral-900 text-white grid place-items-center group-hover:bg-[#FF5A1F] transition-colors">
                    <Play className="w-2.5 h-2.5 fill-current ml-0.5" />
                  </div>
                </a>
              </div>

              {/* Small trust points */}
              <div className="flex flex-wrap items-center gap-2.5 pt-6 border-t border-black/5 text-xs text-neutral-600">
                {TRUST_POINTS.map((pt, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-neutral-200/70 shadow-xs font-medium text-[11px] sm:text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
                    {pt}
                  </span>
                ))}
              </div>
            </div>

            {/* Right Column: Live Interactive 3D Cyber Defense Reactor Animation */}
            <div className="lg:col-span-6 relative flex items-center justify-center">
              <GuardianHeroAnimation />
            </div>

          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 02 — THE 5-STAGE AUTONOMOUS LOOP (CONNECTED WAVE TIMELINE)             */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="scroll-mt-24 relative py-14 sm:py-18 px-5 sm:px-8 bg-white border-b border-black/[0.06] overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          
          {/* Section Header / Eyebrow */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 pb-6 border-b border-black/5">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-mono font-bold text-[#FF5A1F] uppercase tracking-wider mb-1">
                <span>AUTONOMOUS EXECUTION PIPELINE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-[#0B0B0B]">
                The Continuous Defense Loop
              </h2>
            </div>
            <div className="text-xs text-neutral-500 max-w-sm sm:text-right">
              Guardian operates non-stop across all connected nodes without requiring human intervention.
            </div>
          </div>

          {/* Connected Sine Wave Canvas (Desktop) */}
          <div className="relative">
            <div className="hidden lg:block absolute top-6 left-0 right-0 w-full pointer-events-none z-0">
              <svg className="w-full h-10" viewBox="0 0 1000 40" fill="none" preserveAspectRatio="none">
                <path
                  d="M 60 20 C 160 5, 240 35, 300 20 C 380 5, 460 35, 500 20 C 580 5, 660 35, 700 20 C 800 5, 880 35, 940 20"
                  stroke="#FF5A1F"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                  strokeOpacity="0.45"
                />
              </svg>
            </div>

            {/* 5 Connected Step Nodes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-5 relative z-10">
              {AUTONOMOUS_LOOP.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div
                    key={idx}
                    className={`p-5 rounded-2xl transition-all text-left flex flex-col justify-between group ${
                      step.highlight
                        ? "bg-orange-50/60 border-2 border-orange-200/90 shadow-md shadow-orange-500/10"
                        : "bg-[#FAFAFA] border border-black/5 hover:border-black/15"
                    }`}
                  >
                    <div>
                      {/* Step Number + Icon Badge matching reference */}
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-mono font-bold text-neutral-400">
                          {step.step}
                        </span>
                        
                        <div
                          className={`w-11 h-11 rounded-full grid place-items-center transition-all ${
                            step.highlight
                              ? "bg-[#FF5A1F] text-white shadow-lg shadow-[#FF5A1F]/35 ring-4 ring-[#FF5A1F]/20 scale-105"
                              : "bg-white border border-neutral-200 text-neutral-800 group-hover:border-[#FF5A1F]/50 group-hover:text-[#FF5A1F]"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                      </div>

                      {/* Step Title */}
                      <h3 className="text-base sm:text-lg font-bold text-[#0B0B0B] mb-2 group-hover:text-[#FF5A1F] transition-colors">
                        {step.name}
                      </h3>

                      {/* Step Description */}
                      <p className="text-xs text-neutral-600 leading-relaxed font-normal">
                        {step.desc}
                      </p>
                    </div>

                    <div className="pt-4 mt-4 border-t border-black/5 flex items-center text-[11px] font-semibold text-neutral-500 group-hover:text-[#FF5A1F] transition-colors">
                      <span>Phase {step.step} Active</span>
                      <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 02.5 — 5 KEY PROOF METRICS STRIP                                       */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section className="py-6 bg-[#FAFAFA] border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5 sm:gap-4">
            
            {/* Metric 1: < 2.0s */}
            <div className="p-4 rounded-2xl bg-white border border-black/5 flex items-center gap-3.5 text-left shadow-2xs">
              <div className="w-11 h-11 rounded-full bg-red-50 text-red-500 grid place-items-center shrink-0">
                <Zap className="w-5 h-5 fill-current" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-display font-bold text-[#0B0B0B] leading-none mb-1">&lt; 2.0s</div>
                <div className="text-xs font-medium text-neutral-500">Threat Containment</div>
              </div>
            </div>

            {/* Metric 2: < 0.5% */}
            <div className="p-4 rounded-2xl bg-white border border-black/5 flex items-center gap-3.5 text-left shadow-2xs">
              <div className="w-11 h-11 rounded-full bg-blue-50 text-blue-500 grid place-items-center shrink-0">
                <Cpu className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-display font-bold text-[#0B0B0B] leading-none mb-1">&lt; 0.5%</div>
                <div className="text-xs font-medium text-neutral-500">Resource Overhead</div>
              </div>
            </div>

            {/* Metric 3: 100% */}
            <div className="p-4 rounded-2xl bg-white border border-black/5 flex items-center gap-3.5 text-left shadow-2xs">
              <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-500 grid place-items-center shrink-0">
                <Database className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-display font-bold text-[#0B0B0B] leading-none mb-1">100%</div>
                <div className="text-xs font-medium text-neutral-500">Data Sovereignty</div>
              </div>
            </div>

            {/* Metric 4: 42+ */}
            <div className="p-4 rounded-2xl bg-white border border-black/5 flex items-center gap-3.5 text-left shadow-2xs">
              <div className="w-11 h-11 rounded-full bg-orange-50 text-[#FF5A1F] grid place-items-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-display font-bold text-[#0B0B0B] leading-none mb-1">42+</div>
                <div className="text-xs font-medium text-neutral-500">Autonomous Runbooks</div>
              </div>
            </div>

            {/* Metric 5: ₹0 Extra */}
            <div className="p-4 rounded-2xl bg-white border border-black/5 flex items-center gap-3.5 text-left col-span-2 lg:col-span-1 shadow-2xs">
              <div className="w-11 h-11 rounded-full bg-purple-50 text-purple-500 grid place-items-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-display font-bold text-[#0B0B0B] leading-none mb-1">₹0 Extra</div>
                <div className="text-xs font-medium text-neutral-500">SaaS License Tax</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 03 — KEY CAPABILITIES: ONE ENGINE. MULTIPLE LAYERS                     */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="capabilities" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-[#FAFAFA] border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          
          <div className="max-w-3xl text-left mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200/80 text-[10px] sm:text-[11px] font-mono font-semibold text-blue-700 uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span>CORE CAPABILITIES</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-[#0B0B0B] leading-tight mb-4">
              One Engine. Multiple Layers of Protection.
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed">
              Engineered to secure mission-critical workloads, eliminate manual operations, and protect production runtime across all standard development environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {KEY_CAPABILITIES.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <div
                  key={idx}
                  className="p-6 sm:p-7 rounded-3xl bg-white border border-neutral-200/80 shadow-xs hover:shadow-md transition-all text-left flex flex-col justify-between group"
                >
                  <div>
                    <div className={`w-12 h-12 rounded-2xl ${cap.bg} ${cap.accent} grid place-items-center mb-5 group-hover:scale-105 transition-transform`}>
                      <Icon className="w-6 h-6" />
                    </div>

                    <h3 className="text-lg font-bold text-[#0B0B0B] mb-2 group-hover:text-blue-600 transition-colors">
                      {cap.title}
                    </h3>

                    <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                      {cap.desc}
                    </p>
                  </div>

                  <div className="pt-4 mt-6 border-t border-black/5 flex items-center gap-1.5 text-xs font-semibold text-neutral-800">
                    <span>Explore Protection</span>
                    <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 04 — WHY GUARDIAN: MONITORING ISN'T ENOUGH                            */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24 px-5 sm:px-8 bg-white border-b border-black/[0.06] overflow-hidden">
        <div className="max-w-7xl mx-auto">
          
          {/* Header with Title and Right-Hand Description */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 sm:mb-16">
            <div className="max-w-2xl text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF6F0] border border-[#FDE5D6] text-[10px] sm:text-[11px] font-mono font-bold text-[#FF5A1F] uppercase mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
                <span>INCIDENT VELOCITY</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-display font-extrabold text-[#0B0B0B] leading-tight tracking-tight">
                Monitoring Isn't Enough. <br />
                <span className="text-[#FF5A1F]">Speed Changes Outcomes.</span>
              </h2>
            </div>

            <div className="max-w-md text-left lg:border-l-2 lg:border-[#FF5A1F] lg:pl-6 pb-1">
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed font-normal">
                Every minute of delay increases incident impact.<br className="hidden sm:inline" />
                Guardian shortens the response distance.
              </p>
            </div>
          </div>

          {/* Side-by-Side Incident Comparison Cards */}
          <div className="relative">
            {/* Center Glowing Connector Node (Desktop) */}
            <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 items-center pointer-events-none">
              <div className="w-8 h-0.5 border-t border-dashed border-red-300" />
              <div className="relative flex items-center justify-center mx-1">
                <div className="absolute w-12 h-12 rounded-full bg-orange-500/15 blur-md animate-pulse" />
                <div className="w-8 h-8 rounded-full bg-white border border-orange-200 shadow-md flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF5A1F]" />
                </div>
              </div>
              <div className="w-8 h-0.5 border-t-2 border-blue-400" />
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
              
              {/* Left Card: TRADITIONAL APPROACH */}
              <div className="p-6 sm:p-8 rounded-[28px] bg-gradient-to-b from-[#FFFDFD] via-red-50/15 to-red-50/25 border border-red-200/80 shadow-xs flex flex-col justify-between text-left relative overflow-hidden">
                <div>
                  <span className="text-[11px] font-mono font-bold text-red-500 uppercase tracking-wider block mb-8">
                    TRADITIONAL APPROACH
                  </span>

                  {/* 4 Steps Horizontal Flow */}
                  <div className="flex items-center justify-between gap-1 sm:gap-2 mb-8">
                    {/* Step 1: Alert */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-red-200 shadow-xs flex items-center justify-center text-red-500">
                        <Bell className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Alert</span>
                    </div>

                    <div className="flex-1 border-t border-dashed border-red-200/80 -mt-6 mx-1 sm:mx-2" />

                    {/* Step 2: Investigation */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-red-200 shadow-xs flex items-center justify-center text-red-500">
                        <User className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Investigation</span>
                    </div>

                    <div className="flex-1 border-t border-dashed border-red-200/80 -mt-6 mx-1 sm:mx-2" />

                    {/* Step 3: Manual Fix */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-red-200 shadow-xs flex items-center justify-center text-red-500">
                        <Wrench className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Manual Fix</span>
                    </div>

                    <div className="flex-1 border-t border-dashed border-red-200/80 -mt-6 mx-1 sm:mx-2" />

                    {/* Step 4: Recovery */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-red-200 shadow-xs flex items-center justify-center text-red-500">
                        <Clock className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Recovery</span>
                    </div>
                  </div>

                  {/* Rising SVG Curve & Growing Impact Badge */}
                  <div className="relative mt-6 mb-4">
                    {/* Badge at the peak */}
                    <div className="absolute right-4 sm:right-8 top-1 sm:top-2 z-10 bg-white/95 backdrop-blur-sm border border-red-200/90 rounded-2xl px-3.5 py-2 shadow-sm flex items-center gap-2.5">
                      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                      <span className="text-[11px] font-semibold text-neutral-800 leading-tight">
                        Impact<br />keeps growing
                      </span>
                    </div>

                    <svg viewBox="0 0 500 135" className="w-full h-28 sm:h-32 overflow-visible">
                      <defs>
                        <linearGradient id="redAreaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#EF4444" stopOpacity="0.22" />
                          <stop offset="100%" stopColor="#EF4444" stopOpacity="0.0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M 0,115 C 50,118 90,100 130,96 C 170,92 210,105 250,84 C 290,62 330,80 370,54 C 400,34 430,36 470,25 L 470,135 L 0,135 Z"
                        fill="url(#redAreaGrad)"
                      />
                      <path
                        d="M 0,115 C 50,118 90,100 130,96 C 170,92 210,105 250,84 C 290,62 330,80 370,54 C 400,34 430,36 470,25"
                        fill="none"
                        stroke="#EF4444"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      {/* Inflection Dots */}
                      <circle cx="40" cy="116" r="3" fill="#EF4444" />
                      <circle cx="130" cy="96" r="3" fill="#EF4444" />
                      <circle cx="210" cy="103" r="3" fill="#EF4444" />
                      <circle cx="290" cy="68" r="3" fill="#EF4444" />
                      <circle cx="370" cy="54" r="5" fill="#EF4444" stroke="#FFFFFF" strokeWidth="2" />
                    </svg>
                  </div>
                </div>

                {/* Bottom Response Time */}
                <div className="pt-6 mt-6 border-t border-red-200/50 flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-red-50 border border-red-200/60 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-neutral-400">Response Time</div>
                    <div className="text-lg sm:text-xl font-bold text-red-600 font-display">Minutes to Hours</div>
                  </div>
                </div>
              </div>

              {/* Right Card: GUARDIAN APPROACH */}
              <div className="p-6 sm:p-8 rounded-[28px] bg-gradient-to-b from-[#F8FBFF] via-blue-50/15 to-blue-50/30 border-2 border-blue-400/90 shadow-sm flex flex-col justify-between text-left relative overflow-hidden">
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <span className="text-[11px] font-mono font-bold text-blue-600 uppercase tracking-wider block">
                      GUARDIAN APPROACH
                    </span>
                    <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200/90 flex items-center gap-1.5 shadow-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                      AUTONOMOUS
                    </span>
                  </div>

                  {/* 4 Steps Horizontal Flow */}
                  <div className="flex items-center justify-between gap-1 sm:gap-2 mb-8">
                    {/* Step 1: Detect */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-blue-200 shadow-xs flex items-center justify-center text-blue-600">
                        <Search className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Detect</span>
                    </div>

                    <div className="flex-1 border-t-2 border-blue-300 -mt-6 mx-1 sm:mx-2" />

                    {/* Step 2: Analyze */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-blue-200 shadow-xs flex items-center justify-center text-blue-600">
                        <BarChart3 className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Analyze</span>
                    </div>

                    <div className="flex-1 border-t-2 border-blue-300 -mt-6 mx-1 sm:mx-2" />

                    {/* Step 3: Contain */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-blue-200 shadow-xs flex items-center justify-center text-blue-600">
                        <Shield className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Contain</span>
                    </div>

                    <div className="flex-1 border-t-2 border-blue-300 -mt-6 mx-1 sm:mx-2" />

                    {/* Step 4: Remediate */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="w-11 h-11 rounded-full bg-white border border-blue-200 shadow-xs flex items-center justify-center text-blue-600">
                        <RotateCw className="w-4 h-4" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-neutral-800">Remediate</span>
                    </div>
                  </div>

                  {/* Declining Blue SVG Area Chart */}
                  <div className="relative mt-6 mb-4">
                    <svg viewBox="0 0 500 135" className="w-full h-28 sm:h-32 overflow-visible">
                      <defs>
                        <linearGradient id="blueAreaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M 0,65 C 40,65 70,80 120,90 C 180,102 240,114 320,118 C 380,121 440,121 500,121 L 500,135 L 0,135 Z"
                        fill="url(#blueAreaGrad)"
                      />
                      <path
                        d="M 0,65 C 40,65 70,80 120,90 C 180,102 240,114 320,118 C 380,121 440,121 500,121"
                        fill="none"
                        stroke="#3B82F6"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      {/* Inflection Dots dropping to zero */}
                      <circle cx="25" cy="67" r="3.5" fill="#3B82F6" stroke="#FFFFFF" strokeWidth="1.5" />
                      <circle cx="120" cy="90" r="3.5" fill="#3B82F6" stroke="#FFFFFF" strokeWidth="1.5" />
                      <circle cx="230" cy="112" r="3.5" fill="#3B82F6" stroke="#FFFFFF" strokeWidth="1.5" />
                      <circle cx="340" cy="118" r="3.5" fill="#3B82F6" stroke="#FFFFFF" strokeWidth="1.5" />
                      <circle cx="450" cy="121" r="3.5" fill="#3B82F6" stroke="#FFFFFF" strokeWidth="1.5" />
                    </svg>
                  </div>
                </div>

                {/* Bottom Response Time */}
                <div className="pt-6 mt-6 border-t border-blue-200/60 flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-200/60 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-neutral-400">Response Time</div>
                    <div className="text-lg sm:text-xl font-bold text-blue-600 font-display">Sub-2 Seconds</div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 05 — FOUR DEFENSE ENGINES (EXACT 3D ORBITAL SAAS ARCHITECTURE)         */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <FourEnginesOrbit />

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 06 — LIVE GUARDIAN EXPERIENCE & ARCHITECTURE                           */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="architecture" className="scroll-mt-24 py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFD] via-[#FFFFFF] to-[#F8FAFD] border-b border-black/[0.06] text-[#0B0B0B] overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(#4f46e508_1px,transparent_1px)] bg-[size:28px_28px] pointer-events-none opacity-60" />

        <div className="max-w-[1440px] mx-auto relative z-10 text-center">
          
          <div className="max-w-3xl mx-auto mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#EEF2FF] border border-[#DDD6FE] text-[11px] font-mono font-bold text-[#4338CA] uppercase mb-4 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span>LIVE GUARDIAN EXPERIENCE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold text-[#0B0B0B] leading-tight mb-4 tracking-tight">
              See Guardian Respond in <span className="text-[#5B4DF5]">Real Time.</span>
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 leading-relaxed max-w-2xl mx-auto">
              Explore how Guardian continuously surveils infrastructure health, evaluates anomalies, and executes autonomous self-healing.
            </p>
          </div>

          {/* Interactive Realtime Dashboard Slider */}
          <GuardianRealtimeSlider />

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 07 — DEPLOYMENT: DEPLOY GUARDIAN YOUR WAY                              */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="deployments" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-[#FAFAFA] border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          
          <div className="max-w-3xl text-left mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200/80 text-[10px] sm:text-[11px] font-mono font-semibold text-blue-700 uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span>DEPLOYMENT OPTIONS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-[#0B0B0B] leading-tight mb-4">
              Deploy Guardian Your Way.
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed">
              Flexible delivery models engineered to adapt to your organization's compliance, cloud, and security requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {DEPLOYMENT_MODES.map((mode, idx) => (
              <div
                key={idx}
                className={`p-7 rounded-3xl text-left flex flex-col justify-between transition-all ${
                  mode.isPrimary
                    ? "bg-white border-2 border-blue-500 shadow-xl shadow-blue-500/10 relative"
                    : "bg-white border border-neutral-200/80 shadow-xs hover:shadow-md"
                }`}
              >
                {mode.isPrimary && (
                  <div className="absolute -top-3 right-6 px-3 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-mono font-bold">
                    RECOMMENDED
                  </div>
                )}

                <div>
                  <span className="text-[10px] font-mono font-semibold text-neutral-400 block mb-1">
                    MODE {mode.num}
                  </span>
                  <h3 className="text-lg font-bold text-[#0B0B0B] mb-3">
                    {mode.title}
                  </h3>
                  <p className="text-xs text-neutral-600 leading-relaxed mb-6">
                    {mode.desc}
                  </p>

                  <div className="p-4 rounded-2xl bg-neutral-50 border border-neutral-200/60 text-xs text-neutral-700 leading-relaxed mb-6">
                    <span className="font-semibold text-neutral-900 block mb-1">Best For:</span>
                    {mode.bestFor}
                  </div>
                </div>

                <button
                  onClick={() => openConsultationModal(`Guardian Deployment: ${mode.title}`)}
                  className={`w-full py-3 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    mode.isPrimary
                      ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                      : "bg-[#FAFAFA] hover:bg-neutral-100 text-neutral-800 border border-neutral-200"
                  }`}
                >
                  <span>Select {mode.title.split(" ")[0]}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 09 — WHO IS GUARDIAN FOR?                                              */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="use-cases" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-white border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          
          <div className="max-w-3xl text-left mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200/80 text-[10px] sm:text-[11px] font-mono font-semibold text-blue-700 uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span>TARGET AUDIENCE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-[#0B0B0B] leading-tight mb-4">
              Built for Teams That Can't Afford to Wait.
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed">
              Designed for high-growth digital companies where server downtime directly translates to revenue and reputation loss.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TARGET_AUDIENCE.map((aud, idx) => {
              const Icon = aud.icon;
              return (
                <div
                  key={idx}
                  className="p-6 rounded-3xl bg-[#FAFAFA] border border-black/5 hover:border-black/15 transition-all text-left flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center mb-4">
                      <Icon className="w-5 h-5" />
                    </div>

                    <h3 className="text-base font-bold text-[#0B0B0B] mb-2">
                      {aud.title}
                    </h3>

                    <p className="text-xs text-neutral-600 leading-relaxed">
                      {aud.desc}
                    </p>
                  </div>

                  <div className="pt-4 mt-5 border-t border-black/5 flex items-center gap-1 text-xs font-semibold text-neutral-800">
                    <span>Learn More</span>
                    <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 09.5 — ENTERPRISE FAQ SECTION                                         */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="faq" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-[#FAFAFA] border-b border-black/[0.06]">
        <div className="max-w-4xl mx-auto">
          
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200/80 text-[10px] sm:text-[11px] font-mono font-semibold text-[#FF5A1F] uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
              <span>FREQUENTLY ASKED QUESTIONS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-[#0B0B0B] leading-tight mb-4">
              Everything You Need to Know About Guardian.
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed max-w-2xl mx-auto">
              Clear answers regarding deployment models, security isolation, agent overhead, and autonomous healing mechanisms.
            </p>
          </div>

          <div className="space-y-4">
            {GUARDIAN_FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className={`rounded-2xl border transition-all text-left overflow-hidden ${
                    isOpen
                      ? "bg-white border-orange-300 shadow-md shadow-orange-500/5"
                      : "bg-white border-neutral-200/80 hover:border-neutral-300"
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 focus:outline-none"
                  >
                    <span className="text-base sm:text-lg font-bold text-[#0B0B0B]">
                      {faq.q}
                    </span>
                    <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 transition-transform ${
                      isOpen ? "bg-[#FF5A1F] text-white rotate-180" : "bg-neutral-100 text-neutral-600"
                    }`}>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 sm:px-6 pb-6 pt-1 text-sm text-neutral-600 leading-relaxed border-t border-black/5">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-white border border-neutral-200/80 text-center flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-left">
              <div className="text-sm font-bold text-neutral-900">Have a custom infrastructure setup?</div>
              <div className="text-xs text-neutral-500">Our cybersecurity and SRE architects are ready to review your topology.</div>
            </div>
            <button
              onClick={() => openConsultationModal("Guardian Custom FAQ Inquiry")}
              className="px-5 py-2.5 rounded-full bg-[#0B0B0B] hover:bg-neutral-800 text-white text-xs font-semibold transition-all shrink-0"
            >
              Ask an Architect
            </button>
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 10 — FINAL CTA                                                         */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 px-5 sm:px-8 mesh-light text-center">
        <div className="max-w-5xl mx-auto bg-[#0B0B0B] text-white rounded-3xl sm:rounded-[2.5rem] p-8 sm:p-14 relative overflow-hidden border border-white/10 shadow-2xl">
          {/* Background Image: sharp team collaboration meeting photo without blur */}
          <div className="absolute inset-0 z-0">
            <img
              src="/brand/cta-meeting-bg.jpg"
              alt="NeuraVolt Strategy & Engineering Team"
              className="w-full h-full object-cover object-center"
            />
            {/* Contrast overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/75 to-black/90" />
            <div className="absolute inset-0 bg-[#FF5A1F]/10" />
          </div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="w-16 h-16 rounded-3xl bg-white/10 text-[#FF5A1F] border border-white/15 grid place-items-center mx-auto mb-6 shadow-sm">
              <ShieldCheck className="w-9 h-9" />
            </div>

            <h2 className="text-3xl sm:text-5xl font-display font-bold text-white leading-[1.15] tracking-tight mb-6">
              Stop Waiting for the Next Incident.
            </h2>

            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed mb-8 max-w-2xl mx-auto">
              Give your infrastructure an autonomous operational layer with NeuraVolt Guardian.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
              <button
                onClick={() => openConsultationModal("Guardian Final CTA")}
                className="px-8 py-4 rounded-full bg-[#FF5A1F] text-white text-sm sm:text-base font-semibold hover:bg-[#ff6d38] transition-all shadow-lg shadow-[#FF5A1F]/20 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 group cursor-pointer"
              >
                <span>Book a Security Audit</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <a
                href="#overview"
                className="px-8 py-4 rounded-full bg-white/10 text-white text-sm sm:text-base font-semibold border border-white/20 hover:bg-white/20 transition-all shadow-sm"
              >
                <span>Explore Guardian</span>
              </a>
            </div>

            <div className="text-xs text-neutral-400 font-mono">
              Guardian: Autonomous SRE & Active Defense
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
}
